import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Save,
  Key,
  Power,
  PowerOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const Settings = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    secret: '',
    type: 'razorpay',
  });

  const { data: configs = [], isLoading, refetch } = useQuery({
    queryKey: ['apiConfigs'],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/api/configs`);
        if (!res.ok) throw new Error('Failed to fetch configs');
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Error fetching configs:', err);
        return [];
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_BASE}/api/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('API configuration added'); refetch(); resetForm(); },
    onError: (err) => toast.error('Create failed: ' + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_BASE}/api/configs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('API configuration updated'); refetch(); resetForm(); },
    onError: (err) => toast.error('Update failed: ' + err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_BASE}/api/configs/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to delete');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('API configuration deleted'); refetch(); },
    onError: (err) => toast.error('Delete failed: ' + err.message),
  });

  const toggleMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_BASE}/api/configs/${id}/toggle`, {
        method: 'POST',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to toggle');
      }
      return res.json();
    },
    onSuccess: () => { toast.success('Status toggled'); refetch(); },
    onError: (err) => toast.error('Toggle failed: ' + err.message),
  });

  const resetForm = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
    setFormData({ name: '', key: '', secret: '', type: 'razorpay' });
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData({ name: '', key: '', secret: '', type: 'razorpay' });
    setIsDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      key: item.key || '',
      secret: item.secret || '',
      type: item.type || 'razorpay',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.key || !formData.secret) {
      toast.error('All fields are required');
      return;
    }
    const data = { ...formData, isActive: false };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleToggle = (item) => {
    toggleMutation.mutate(item.id);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this configuration?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 shadow-xl">
        <h1 className="text-3xl font-bold text-white flex items-center gap-2">
          <Key className="w-8 h-8" /> API Settings
        </h1>
        <p className="text-white/80">Manage payment gateway API configurations</p>
        <div className="absolute top-2 right-4 text-white/30 text-sm">
          Backend: {API_BASE}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white gap-2">
          <Plus className="w-4 h-4" /> Add API Key
        </Button>
      </div>

      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-400">
                    No API configurations found.
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((item) => (
                  <TableRow key={item.id} className="hover:bg-amber-50/50">
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-xs">{item.key.substring(0, 8)}...</TableCell>
                    <TableCell><Badge variant="outline">{item.type}</Badge></TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(item)} title="Toggle Active">
                          {item.isActive ? <Power className="w-4 h-4 text-amber-500" /> : <PowerOff className="w-4 h-4 text-gray-400" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Edit">
                          <Edit className="w-4 h-4 text-indigo-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} title="Delete">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit API Config' : 'Add API Config'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input
                placeholder="e.g., Razorpay Live"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Key (Public)</Label>
              <Input
                placeholder="rzp_live_..."
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              />
            </div>
            <div>
              <Label>Secret (Private)</Label>
              <Input
                type="password"
                placeholder="rzp_live_..."
                value={formData.secret}
                onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
              />
            </div>
            <div>
              <Label>Type</Label>
              <Input
                placeholder="razorpay"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Save className="w-4 h-4 mr-2" />
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
